import { Button, Drawer, Result } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import './App.css';
import { AppSidebar } from './components/AppSidebar';
import { BrandMark } from './components/BrandMark';
import { CommandPalette, useCommandPaletteHotkey } from './components/CommandPalette';
import { Topbar } from './components/Topbar';
import { useAppDispatch } from './app/hooks';
import { fetchCountries } from './features/countrySlice';
import { useAuth, hasAuthParams } from "react-oidc-context";
import { hasGlobalAdminRole } from './utils/jwt';
import { setAccessToken, clearAccessToken } from './authConfig/token';
import { useTranslation } from 'react-i18next';
import { brand } from './config/brand';

/** Remembers the rail's width between sessions; it is a workspace preference. */
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

function App() {

    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const auth = useAuth();
    const location = useLocation();

    const isGlobalAdmin = hasGlobalAdminRole(auth.user?.access_token);

    // The API layer reads the token from storage (see `server.ts`), so nothing
    // that calls the API may mount before it has been written. Child effects
    // run before the parent's, so gating the layout on `isAuthenticated` alone
    // let a page's mount fetch go out unauthenticated on a reload.
    const [tokenReady, setTokenReady] = useState(false);

    const [collapsed, setCollapsed] = useState(
        () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
    );
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);

    const openPalette = useCallback(() => setPaletteOpen(true), []);
    useCommandPaletteHotkey(openPalette);

    const toggleCollapsed = () => {
        setCollapsed(current => {
            localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(!current));
            return !current;
        });
    };

    useEffect(() => {
        if (!hasAuthParams() &&
            !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
            auth.signinRedirect();}
    },[auth.isAuthenticated, auth.activeNavigator, auth.isLoading, auth.signinRedirect]);

    useEffect(() => {
        if (tokenReady) {
            dispatch(fetchCountries());
        }
    }, [tokenReady])

    useEffect(() => {
        if (auth.isAuthenticated && isGlobalAdmin) {
            setAccessToken(auth.user?.access_token ?? '');
            setTokenReady(true);
        } else if (!auth.isLoading) {
            // While the session is still being restored the stored token is
            // left alone: clearing it here would strand any request issued
            // before the restore completes.
            clearAccessToken();
            setTokenReady(false);
        }
    }, [auth, isGlobalAdmin]);

    useEffect(() =>  {
        return auth.events.addAccessTokenExpiring(async () => {
            await auth.signinSilent();
            const token = auth.user?.access_token;
            if (hasGlobalAdminRole(token)) {
                setAccessToken(token ?? '');
            } else {
                clearAccessToken();
            }
        })
    }, [auth.events,auth.signinSilent]);

    if (auth.isAuthenticated && !isGlobalAdmin) {
        return (
            <div className="App">
                <div className="app-standalone">
                    <Result
                        status="403"
                        title={t('accessDenied.title')}
                        subTitle={t('accessDenied.subTitle')}
                        extra={
                            <Button type="primary" onClick={() => auth.signoutRedirect()}>
                                {t('actions.signOut')}
                            </Button>
                        }
                    />
                </div>
            </div>
        );
    }

    if (!auth.isAuthenticated || !tokenReady) {
        return (
            <div className="App">
                <div className="boot-screen">
                    <span className="boot-screen__mark">
                        <BrandMark />
                    </span>
                    <span className="boot-screen__label">{brand.name}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="App">
            <div className="app-shell">
                <AppSidebar collapsed={collapsed} onToggleCollapse={toggleCollapsed} />

                {/* Below the `lg` breakpoint the rail is hidden and the same
                    navigation is reached through this drawer. It used to
                    collapse to nothing with no trigger, which left the app
                    unnavigable on a narrow screen. */}
                <Drawer
                    placement="left"
                    closable={false}
                    width={272}
                    bodyStyle={{ padding: 0 }}
                    visible={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                >
                    <AppSidebar variant="drawer" onNavigate={() => setDrawerOpen(false)} />
                </Drawer>

                <div className="app-main">
                    <Topbar
                        onOpenMenu={() => setDrawerOpen(true)}
                        onOpenCommandPalette={openPalette}
                    />
                    <main className="app-content">
                        {/* Keyed on the section, not the full path: switching
                            sections replays the entrance, while drilling into a
                            record inside one keeps the subtree mounted. */}
                        <div className="page" key={location.pathname.split("/")[1] ?? ""}>
                            <Outlet />
                        </div>
                    </main>
                </div>
            </div>

            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </div>
    );
}

export default App;
